import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface LoanOfficer {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  license_number: string | null;
  license_state: string | null;
  photo_url: string | null;
  logo_url: string | null;
  bio: string | null;
  ad_image_url: string | null;
  site_id: string | null;
  is_active: boolean;
}

interface LoanOfficerAuthContextType {
  loanOfficer: LoanOfficer | null;
  isLoanOfficer: boolean;
  loading: boolean;
  checked: boolean;
  refetch: () => Promise<void>;
}

const LoanOfficerAuthContext = createContext<LoanOfficerAuthContextType>({
  loanOfficer: null,
  isLoanOfficer: false,
  loading: true,
  checked: false,
  refetch: async () => {},
});

export function LoanOfficerAuthProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [loanOfficer, setLoanOfficer] = useState<LoanOfficer | null>(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);

  const fetchLoanOfficer = async () => {
    if (!user) {
      setLoanOfficer(null);
      setLoading(false);
      setChecked(true);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('loan_officers')
        .select('id, first_name, last_name, email, phone, company_name, license_number, license_state, photo_url, logo_url, bio, ad_image_url, site_id, is_active')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching loan officer:', error);
        setLoanOfficer(null);
      } else {
        setLoanOfficer(data);
      }
    } catch (err) {
      console.error('Error in loan officer auth:', err);
      setLoanOfficer(null);
    } finally {
      setLoading(false);
      setChecked(true);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchLoanOfficer();
    }
  }, [user, authLoading]);

  const refetch = async () => {
    setLoading(true);
    await fetchLoanOfficer();
  };

  return (
    <LoanOfficerAuthContext.Provider
      value={{
        loanOfficer,
        isLoanOfficer: !!loanOfficer,
        loading: authLoading || loading,
        checked,
        refetch,
      }}
    >
      {children}
    </LoanOfficerAuthContext.Provider>
  );
}

export function useLoanOfficerAuth() {
  return useContext(LoanOfficerAuthContext);
}
